import {
  Agency,
  Id,
  Request,
  RequestQuoteHotel,
  RequestQuoteHotelRoom,
  RequestQuoteHotelRoomGuest,
  RequestTicketSelectedSituation,
  RequestTicketStatus,
  RequestTicketUserData,
  ResumeHotel
} from 'src/types'
import { useSelector } from 'react-redux'

import { useMemo } from 'react'
import { ApplicationState } from 'src/store'
import moment from 'moment'

export type SelectedHotel = Pick<
  RequestQuoteHotel,
  | 'address'
  | 'brand_name'
  | 'category_stars'
  | 'hotel_image_url'
  | 'laundry'
  | 'parking'
  | 'pool'
  | 'restaurant'
  | 'checkin_date'
  | 'checkout_date'
> &
  Pick<
    RequestQuoteHotelRoom,
    | 'category'
    | 'guests'
    | 'integration_type'
    | 'pension'
    | 'request_quote_hotel_room_hash'
    | 'total_value'
  > & {
    agency_data?: Agency
    checkin_date_format: string
    checkout_date_format: string
    daily: number
    eticket_code?: string
    request_ticket_id?: Id
    situation: RequestTicketSelectedSituation
    status?: RequestTicketStatus
    user_data: RequestTicketUserData[]
  }

function useGetSelectedHotels(request: Request): SelectedHotel[] {
  const {
    hotels: { searches }
  } = useSelector((state: ApplicationState) => ({
    hotels: state.hotels
  }))

  const selectedHotels = useMemo<SelectedHotel[]>(() => {
    const items: SelectedHotel[] = []
    if (!request.request_id) {
      const rooms: SelectedHotel[] = []

      request.full_request_hotel.forEach(fullRequestHotelItem => {
        const foundHotelsSearches = Object.values(searches).reduce<
          ResumeHotel[]
        >((acc, { list }) => {
          const foundHotel = list.find(
            ({ request_quote_hotel_hash }) =>
              fullRequestHotelItem &&
              request_quote_hotel_hash ===
                fullRequestHotelItem.request_quote_hotel_hash
          )

          return !foundHotel ? acc : [...acc, foundHotel]
        }, [])

        foundHotelsSearches.forEach(foundHotels => {
          fullRequestHotelItem.rooms.forEach(roomItem => {
            const selectedRoom = foundHotels.request_quote_hotel_rooms.find(
              roomFind =>
                roomFind.request_quote_hotel_room_hash ===
                roomItem.request_quote_hotel_room_hash
            )

            if (selectedRoom) {
              rooms.push({
                address: foundHotels.address,
                brand_name: foundHotels.brand_name,
                category_stars: foundHotels.category_stars,
                category: selectedRoom.category,
                checkin_date_format: moment
                  .unix(foundHotels.checkin_date)
                  .utc()
                  .format('DD/MM/YYYY'),
                checkin_date: foundHotels.checkin_date,
                checkout_date_format: moment
                  .unix(foundHotels.checkout_date)
                  .utc()
                  .format('DD/MM/YYYY'),
                checkout_date: foundHotels.checkout_date,
                daily: moment
                  .unix(foundHotels.checkout_date)
                  .utc()
                  .diff(moment.unix(foundHotels.checkin_date).utc(), 'days'),
                guests: roomItem.guests,
                hotel_image_url: foundHotels.hotel_image_url,
                integration_type: selectedRoom.integration_type,
                laundry: null,
                parking: null,
                pension: selectedRoom.pension,
                pool: null,
                request_quote_hotel_room_hash:
                  selectedRoom.request_quote_hotel_room_hash,
                restaurant: null,
                request_ticket_id: null,
                situation: null,
                total_value: selectedRoom.total_value,
                user_data: roomItem.guests.map(guest => {
                  const user = request.request_user_data.find(
                    user_data => user_data.user_id === guest.user_id
                  )
                  return {
                    request_ticket_user_data_id: null,
                    request_ticket_id: null,
                    included: true,
                    owner: guest.owner,
                    traveler: user?.traveler,
                    user_id: guest.user_id
                  }
                }) as RequestTicketUserData[]
              })
            }
          })
        })
      })

      return rooms
    }

    request.request_tickets
      .filter(
        requestTicketItem =>
          requestTicketItem.class_type === 'SERVICE' &&
          requestTicketItem.request_ticket_type === 'HOTEL'
      )
      .forEach(requestTicketItem => {
        const {
          eticket_code,
          request_quote_hotel_room: {
            category,
            pension,
            request_quote_hotel_room_id,
            request_quote_hotel: {
              request_quote: { request_quote_type: integration_type },
              address,
              brand_name,
              category_stars,
              checkin_date,
              checkout_date,
              hotel_image_url,
              laundry,
              parking,
              pool,
              request_quote,
              restaurant
            }
          },
          request_ticket_id,
          situation
        } = requestTicketItem.request_ticket_hotel_selected

        const receiptCost = requestTicketItem.request_ticket_receipts[0].cost

        items.push({
          address,
          agency_data: request_quote.customer_supplier.supplier.agency,
          brand_name,
          category_stars,
          category,
          checkin_date,
          checkin_date_format: moment
            .unix(checkin_date)
            .utc()
            .format('DD/MM/YYYY'),
          checkout_date,
          checkout_date_format: moment
            .unix(checkout_date)
            .utc()
            .format('DD/MM/YYYY'),
          daily: moment
            .unix(checkout_date)
            .utc()
            .diff(moment.unix(checkin_date).utc()),
          eticket_code,
          guests:
            requestTicketItem.request_ticket_user_data.map<RequestQuoteHotelRoomGuest>(
              requestTicketUserDataItem => ({
                owner: requestTicketUserDataItem.owner,
                user_id: requestTicketUserDataItem.traveler.user_id
              })
            ),
          hotel_image_url,
          integration_type,
          laundry,
          parking,
          pension,
          pool,
          request_quote_hotel_room_hash: request_quote_hotel_room_id,
          request_ticket_id,
          restaurant,
          situation,
          status: requestTicketItem.status,
          total_value: receiptCost,
          user_data: requestTicketItem.request_ticket_user_data
        })
      })

    return items
  }, [searches, request])

  return selectedHotels
}

export default useGetSelectedHotels
